import type { Role } from '../types';

/**
 * The staff support session marker (SC-2948), persisted beside the auth tokens.
 *
 * Written when staff enter a client org via `/auth/switch-org` under a support
 * grant; cleared on exit, logout, and every other identity transition (via
 * `clearSessionState`). It carries two things the wire can't give us:
 *
 * - **What to show in the banner** — `/me` lists the support membership but with
 *   nothing marking it as support, so the org/role/expiry are captured from the
 *   grant row at entry time.
 * - **The way back** — `platformRefreshToken` is the staff-scope refresh token
 *   held BEFORE entering. The BE leaves it valid on switch (no rotation), so
 *   when the grant expires or is revoked mid-session — the support-scoped
 *   refresh then fails too — the client can refresh with this instead and land
 *   back in the platform scope, rather than signing staff out entirely.
 *
 * Lives in localStorage (same store as the token pair) so a reload inside the
 * client org keeps the banner and the recovery path.
 */
export interface StoredSupportSession {
  organizationId: string;
  organizationName: string;
  role: Role;
  expiresAt: string;
  platformRefreshToken: string;
}

/** The public face of a support session — everything but the token. */
export type SupportSessionInfo = Omit<StoredSupportSession, 'platformRefreshToken'>;

const KEY = 'support_session';

function isStoredSupportSession(v: unknown): v is StoredSupportSession {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.organizationId === 'string' &&
    typeof o.organizationName === 'string' &&
    typeof o.role === 'string' &&
    typeof o.expiresAt === 'string' &&
    typeof o.platformRefreshToken === 'string'
  );
}

export const supportSession = {
  read: (): StoredSupportSession | null => {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      return isStoredSupportSession(parsed) ? parsed : null;
    } catch (e) {
      if (e instanceof SyntaxError) return null;
      throw e;
    }
  },
  write: (s: StoredSupportSession): void => {
    localStorage.setItem(KEY, JSON.stringify(s));
  },
  clear: (): void => {
    localStorage.removeItem(KEY);
  },
};

export function toSupportInfo(s: StoredSupportSession): SupportSessionInfo {
  return {
    organizationId: s.organizationId,
    organizationName: s.organizationName,
    role: s.role,
    expiresAt: s.expiresAt,
  };
}

/**
 * How a platform-session recovery went:
 * - `recovered`: back in the staff scope, tokens swapped, `support:ended` fired.
 * - `retry`: couldn't reach the server (offline, 5xx) — nothing changed, the
 *   stash is kept, try again later. Never a reason to sign out.
 * - `superseded`: the session changed while we waited (signed out, or a new
 *   login) — nothing committed; the newer session is left alone.
 * - `unrecoverable`: no support session to recover, or the staff token was
 *   rejected — a genuine sign-out.
 */
export type RecoveryOutcome = 'recovered' | 'retry' | 'superseded' | 'unrecoverable';

/**
 * Tokens live in localStorage, shared by every tab. When one tab changes the
 * session's org scope it writes this key; the `storage` event tells the OTHER
 * tabs to drop what they're showing and re-hydrate, so no tab keeps rendering
 * one client while its requests go out as another.
 */
export const SCOPE_CHANGED_KEY = 'session_scope_changed';

export function announceScopeChange(): void {
  localStorage.setItem(SCOPE_CHANGED_KEY, `${Date.now()}:${Math.random()}`);
}

/** Why a support session ended without the operator pressing Exit. */
export type SupportEndReason = 'expired' | 'lost';

/**
 * Fired on `window` by the API client after it has recovered the platform
 * session (tokens already swapped). AuthProvider re-hydrates and routes.
 */
export const SUPPORT_ENDED_EVENT = 'support:ended';

export interface SupportEndedDetail {
  organizationName: string;
  reason: SupportEndReason;
}
